/**
 * Employee API Contract
 *
 * Endpoints for employee management:
 * - Create: Add new employee (handles User + Employment creation)
 * - Update: Modify existing employee
 * - CSV Import: Bulk import from CSV (preview + confirm phases)
 *
 * Security: companyId is extracted from JWT, NOT from request body.
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { ListEmployeesInputSchema, EmployeeResponseSchema, } from "@hourly/api/employee";
import { CreateEmployeeInputSchema, UpdateEmployeeInputSchema, EmployeeDetailResponseSchema, } from "@hourly/api/employment";
import { CsvEmployeePreviewInputSchema, CsvEmployeePreviewResponseSchema, CsvEmployeeConfirmInputSchema, CsvEmployeeConfirmResponseSchema, } from "@hourly/api/csvEmployeeUpload";
import { uuidSchema } from "@hourly/api/validation-common";
// ============================================
// Contracts
// ============================================
/**
 * GET /employees
 * List employees for the authenticated user's company
 *
 * CompanyId is extracted from JWT token.
 * Returns all employees matching the optional filters.
 */
export const listEmployees = oc
    .route({
    method: "GET",
    path: "/employee/list",
    summary: "List employees for company",
    description: "Returns all employees for the authenticated user's company. " +
        "Supports filtering by location, job roles, skills, availability time range, " +
        "and active/inactive status. Defaults to active employees only.",
    tags: ["Employees"],
})
    .input(ListEmployeesInputSchema)
    .output(z.array(EmployeeResponseSchema));
/**
 * POST /employees
 * Create a new employee
 *
 * Behavior varies based on email existence:
 * - Email doesn't exist: Create User + Employment
 * - Email exists (different company): Create Employment only
 * - Email exists (same company, active): 409 Conflict
 * - Email exists (same company, inactive): Reactivate Employment
 *
 * CompanyId is extracted from JWT token.
 */
export const createEmployee = oc
    .route({
    method: "POST",
    path: "/employees",
    summary: "Create a new employee",
    description: "Creates a new employee for the authenticated user's company. " +
        "If the email already exists as a User, creates an Employment link. " +
        "If an inactive Employment exists, it will be reactivated.",
    tags: ["Employees"],
    successStatus: 201,
})
    .input(CreateEmployeeInputSchema)
    .output(EmployeeDetailResponseSchema.extend({
    action: z.enum(["CREATED_NEW_USER", "LINKED_EXISTING_USER", "REACTIVATED"]),
}));
/**
 * PATCH /employees/{employmentId}
 * Update an existing employee
 *
 * Updates both User fields (firstName, lastName, etc.) and
 * Employment fields (employmentType, companyEmail, etc.)
 */
export const updateEmployee = oc
    .route({
    method: "PATCH",
    path: "/employees/{employmentId}",
    summary: "Update an employee",
    description: "Updates an existing employee's information. " +
        "Can update User fields and Employment fields. " +
        "Use null to clear nullable fields.",
    tags: ["Employees"],
})
    .input(z
    .object({
    employmentId: uuidSchema,
})
    .merge(UpdateEmployeeInputSchema))
    .output(EmployeeDetailResponseSchema);
/**
 * POST /employees/csv/preview
 * Preview CSV employee import
 *
 * Parses the uploaded CSV file and returns:
 * - Valid rows with their status (new/link/reactivate)
 * - Invalid rows with error messages
 * - Duplicate rows (active employments)
 * - Unmapped location/job role names
 * - Auto-detected column mappings
 */
export const csvEmployeePreview = oc
    .route({
    method: "POST",
    path: "/employees/csv/preview",
    summary: "Preview CSV employee import",
    description: "Parses an uploaded CSV file and returns validation results. " +
        "Shows which rows are valid, invalid, or duplicates. " +
        "Auto-detects column mappings from headers.",
    tags: ["Employees", "CSV Import"],
})
    .input(CsvEmployeePreviewInputSchema)
    .output(CsvEmployeePreviewResponseSchema);
/**
 * POST /employees/csv/confirm
 * Confirm CSV employee import
 *
 * Imports the validated employees from the CSV file.
 * Runs in a transaction for atomicity.
 */
export const csvEmployeeConfirm = oc
    .route({
    method: "POST",
    path: "/employees/csv/confirm",
    summary: "Confirm CSV employee import",
    description: "Imports employees from the previously previewed CSV file. " +
        "Creates Users, Employments, and handles reactivations. " +
        "Runs in a database transaction for atomicity.",
    tags: ["Employees", "CSV Import"],
})
    .input(CsvEmployeeConfirmInputSchema)
    .output(CsvEmployeeConfirmResponseSchema);
/**
 * GET /employees/export/csv
 * Export employees as CSV
 *
 * Returns CSV string with employee data for download.
 * Use isActive to export current or former team members.
 * CompanyId is extracted from JWT token.
 */
export const exportEmployeesCsv = oc
    .route({
    method: "GET",
    path: "/employees/export/csv",
    summary: "Export employees as CSV",
    description: "Exports employee data as a CSV string for the authenticated user's company. " +
        "Use isActive=true for current team members or isActive=false for former team members.",
    tags: ["Employees", "CSV Export"],
})
    .input(z.object({
    isActive: z
        .stringbool({ truthy: ["true"], falsy: ["false"] })
        .optional(),
}))
    .output(z.string());
// ============================================
// Contract Router
// ============================================
export const employeeContract = {
    list: listEmployees,
    create: createEmployee,
    update: updateEmployee,
    csvPreview: csvEmployeePreview,
    csvConfirm: csvEmployeeConfirm,
    exportCsv: exportEmployeesCsv,
};
//# sourceMappingURL=employee.js.map